import React from 'react';
import { MapPinIcon, PencilSquareIcon, FlagIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { VisibilityState } from '@tanstack/react-table';
import { formatDate, formatDateTime } from './dynamicColumns';
import { combineDupePros, openDupeProSearch } from './ediDupes';
import EdiRowActions from '../components/EdiRowActions';
import { EdiFieldDefinition } from '../hooks/useEdiOrdersFieldDefinitions';

/**
 * Column generation for the Pending EDI Orders drill-down TanTable, from the
 * edi_orders field-definition registry. Unlike the orders/dispatch generator, the
 * accessor is the field's `source_fieldname` (the raw key the bespoke query returns),
 * NOT getApiFieldName(fieldname) — the edi_orders scope has no orders-style name mapping.
 *
 * The drill query returns every column in each row, so all defined columns are built;
 * add/remove is client-side visibility (see EDI_DEFAULT_COLUMNS / buildEdiDefaultVisibility).
 */

/** Row accessor for a field def (raw column key), with a safe fallback. */
const accessorFor = (f: EdiFieldDefinition): string =>
  (f.source_fieldname || f.fieldname || '').toString();

/** Default-visible columns, in display order (raw `source_fieldname` keys). `editype`
 *  is pinned left (see the page's lockedColumns) and `dupeprolist` renders as a COUNT
 *  (see the special column below). Every other registered field stays available via the
 *  column picker but hidden by default. */
export const EDI_DEFAULT_COLUMNS: string[] = [
  'editype',           // pinned left (future actions hang off this)
  'dupeprolist',       // rendered as a dupe COUNT, not the pro list
  'processdatetime',   // EDI Rcv Date
  'container',
  'sizetype',          // combined Size/Type (individual size/equiptype stay addable)
  'movetype',
  'scac',
  'bol',
  'booking',
  'po',
  'billtorefno',       // Ref No
  'ediportoforigin',   // Port of Origin (mapped CMS port name, else Map Port)
  'ediportofdischarge',// Port of Discharge
  'hazmat',
  'rateinfo',          // Rate
  'lbs',
  'pickupname',
  'deliveryname',
  'terminatename',
  'acceptedby',        // shown for accepted rows (blank otherwise)
  'acceptdatetime',    // EDI Accept Date
];

const fmtNumber = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString();
};

/**
 * Columns that need a bespoke definition rather than the type-driven default.
 * `dupeprolist` shows the NUMBER of duplicate pros — the union of dupeprolist +
 * dupeprolist2 — as a link that opens search results for those pros in a new tab.
 * Blank when none; sorts/filters numerically on the count.
 */
const SPECIAL_COLUMN_BUILDERS: Record<string, (field: EdiFieldDefinition) => ColumnDef<any>> = {
  dupeprolist: (field) => ({
    id: 'dupeprolist',
    accessorFn: (row: any) => combineDupePros(row?.dupeprolist, row?.dupeprolist2).length,
    header: field.abbreviation || field.displayname || 'Dupes',
    enableSorting: true,
    enableColumnFilter: true,
    cell: (info) => {
      const n = info.getValue() as number;
      if (!n) return '';
      const row = info.row.original as any;
      const pros = combineDupePros(row?.dupeprolist, row?.dupeprolist2);
      return (
        <button
          type="button"
          onClick={() => openDupeProSearch(pros)}
          className="text-blue-600 dark:text-blue-400 hover:underline tabular-nums"
          title={`View ${n} duplicate pro${n === 1 ? '' : 's'} in a new tab`}
        >
          {n}
        </button>
      );
    },
    // Centered, unlike the right-aligned numeric default: this is a one- or
    // two-digit click target, and hard against the right edge it reads as part
    // of the next column.
    meta: { align: 'center' },
  }),
};

function cellFor(field: EdiFieldDefinition): ColumnDef<any>['cell'] {
  switch (field.type) {
    case 'date':
      return (info) => formatDate(info.getValue() as string);
    case 'datetime':
      return (info) => formatDateTime(info.getValue() as string);
    case 'number':
    case 'decimal':
      return (info) => fmtNumber(info.getValue());
    default: {
      // Long free text (comments, rate, commodity, dupe lists) — truncate w/ tooltip.
      return (info) => {
        const val = info.getValue() as string;
        if (val && typeof val === 'string' && val.length > 60) {
          return <span title={val}>{val.slice(0, 60)}…</span>;
        }
        return val ?? '';
      };
    }
  }
}

/**
 * Build TanStack column defs for every displayable edi_orders field, ordered with the
 * default columns first (in EDI_DEFAULT_COLUMNS order), then the rest alphabetically by
 * display name. Column id === accessor === source_fieldname.
 */
export interface BuildEdiColumnsOptions {
  /** When set, the editype ("EDI Type") cell renders as a link opening EDI Info for that row's recno. */
  onEdiInfoClick?: (recno: number) => void;
  /** When set, an unmapped location cell (edi_locations code present, no mapped CMS name)
   *  renders a red "Map Location" link opening the edi_locations editor for that code. */
  onMapLocation?: (ediLocationRecno: number) => void;
  /** When set, an unmapped Size/Type cell (edi_equipmentmap recno present, no size/type)
   *  renders a red "Map Size/Type" link opening the equipment-map editor for that recno. */
  onMapEquipment?: (equipmentMapRecno: number) => void;
  /** When set, an unmapped port cell (a raw EDI UN/LOCODE with no CMS port name) renders a
   *  red "Map Port" link opening the port-map modal seeded with that unlocode. */
  onMapPort?: (unlocode: string) => void;
  /** When set, a SCAC cell backed by an edi_scacmap row (mappedscac recno) renders a red
   *  "Map SCAC" link when unresolved, opening the edi_scacmap editor for that recno. */
  onMapScac?: (ediScacMapRecno: number) => void;
  /** Per-record actions. When any is set, a right-pinned "Actions" column is appended with
   *  Accept / Reject / Delete (blank rows) or Clear / Delete (accepted rows). */
  onAcceptRow?: (recno: number) => void;
  onRejectRow?: (recno: number) => void;
  onDeleteRow?: (recno: number) => void;
  onClearRow?: (recno: number) => void;
  /** Get Template — the Actions cell shows it only when the row is eligible (blank
   *  template + mapped delivery location, deliveryrecno > 0). */
  onGetTemplateRow?: (recno: number) => void;
  /** Attach to an existing pro — the Actions cell shows it only when eligible (accepted +
   *  no missing product). */
  onAttachRow?: (recno: number) => void;
  /** Create a NEW pro from this EDI order — the Actions cell shows it only when eligible
   *  (accepted + valid template + no missing product). */
  onCreateRow?: (recno: number) => void;
  /** Edit/clear an already-set template — the Template column renders the value as a link
   *  that reopens the picker (pick another, or Clear Template). */
  onEditTemplate?: (recno: number) => void;
}

/** A pending EDI order can Get Template when its template is blank and its delivery
 *  location is mapped to a cms.locations record (deliveryrecno > 0). */
export function canGetTemplate(row: any): boolean {
  return !((row?.template as string) || '') && Number(row?.deliveryrecno) > 0;
}

/**
 * A pending EDI order can Attach to an existing pro once it's ACCEPTED and has NO missing
 * product — the same gate V1 uses for Create Orders (you can't finalize while products are
 * unmapped). meadwestwhseedi additionally requires mapped locations (V1 fv_ediattachpro,
 * formatview.pl:3484). pronumber is always 0 on the board, so it isn't re-checked here.
 */
export function canAttach(row: any): boolean {
  const accepted = (((row?.acceptreject as string) || '') === 'A');
  const noMissingProduct = !Number(row?.missingproduct || 0);
  const meadwestBlocked = row?.edihandle === 'meadwestwhseedi' && Number(row?.missinglocdata || 0) > 0;
  return accepted && noMissingProduct && !meadwestBlocked;
}

/**
 * A pending EDI order can Create a new pro once it's ACCEPTED, has a VALID template
 * (invalidtemplate !== 'Y'), and has NO missing product (V1 edi_createallorders skip filter).
 * Ports-mapped + the IMPORT-ETA requirement are additionally enforced server-side and surfaced
 * in the create dialog. pronumber is always 0 on the board, so it isn't re-checked here.
 */
export function canCreate(row: any): boolean {
  const accepted = (((row?.acceptreject as string) || '') === 'A');
  const hasTemplate = !!(((row?.template as string) || '').trim());
  const validTemplate = (((row?.invalidtemplate as string) || 'N') !== 'Y');
  const noMissingProduct = !Number(row?.missingproduct || 0);
  return accepted && hasTemplate && validTemplate && noMissingProduct;
}

/**
 * A "mapped CMS location" sub-line for the detail views (EDI Info modal, EDI Order
 * drawer): the mapped location name (green, pin icon) when mapped — with a pencil to
 * re-map if the mapping is wrong; otherwise, if an edi_locations code is present, the
 * red "Map Location" button; else nothing. The pencil/Map action both open the SAME
 * edi_locations recno. `mapCode` may be int or string "0", so coerce with Number(...) > 0.
 */
export function renderMappedLocationLine(
  mapName: unknown,
  mapCode: unknown,
  onMapLocation?: (recno: number) => void,
): React.ReactNode {
  const nm = mapName == null ? '' : String(mapName);
  const recno = Number(mapCode) || 0;
  if (nm) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
        <MapPinIcon className="w-3 h-3 shrink-0" />
        {nm}
        {recno > 0 && onMapLocation && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMapLocation(recno); }}
            className="ml-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            title="Re-map this location"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }
  if (recno > 0 && onMapLocation) return renderLocationValue('', recno, onMapLocation);
  return null;
}

/** Location display cells → the row key holding their edi_locations code (V1 fv_edilocation). */
const LOCATION_CODE_FIELD: Record<string, string> = {
  pickupname: 'pickupcode',
  deliveryname: 'deliverycode',
  terminatename: 'terminatecode',
};

/**
 * Render a location value the way V1 fv_edilocation does: the mapped CMS location name
 * when present; otherwise, if the row carries an edi_locations code, a red "Map Location"
 * link (bg-brand-red, matching the order info page) that opens the mapping editor; else blank.
 * `code` may arrive as an int or the string "0", so coerce with Number(...) > 0.
 */
/** The red "map me" action button (bg-brand-red) shared by the location + size/type
 *  cells; stops propagation so it doesn't trigger a parent row/field click. */
function mapActionButton(recno: number, onMap: (recno: number) => void, label: string): React.ReactNode {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onMap(recno); }}
      className="bg-brand-red text-white rounded px-1.5 py-0.5 text-xs font-medium hover:opacity-90"
      title={label}
    >
      {label}
    </button>
  );
}

export function renderLocationValue(
  name: unknown,
  code: unknown,
  onMapLocation?: (recno: number) => void,
): React.ReactNode {
  const nm = name == null ? '' : String(name);
  if (nm) return nm;
  const recno = Number(code) || 0;
  if (recno > 0 && onMapLocation) return mapActionButton(recno, onMapLocation, 'Map Location');
  return '';
}

/**
 * Combined "size equiptype" for the board (card + table): the combined value when
 * either is present; otherwise, if an edi_equipmentmap recno is present, a red "Map
 * Size/Type" button opening the equipment editor; else blank. Mirrors renderLocationValue.
 */
export function renderSizeTypeValue(
  size: unknown,
  equiptype: unknown,
  equipmentMapCode: unknown,
  onMapEquipment?: (recno: number) => void,
): React.ReactNode {
  const combined = [size, equiptype].map((v) => (v == null ? '' : String(v).trim())).filter(Boolean).join(' ');
  if (combined) return combined;
  const recno = Number(equipmentMapCode) || 0;
  if (recno > 0 && onMapEquipment) return mapActionButton(recno, onMapEquipment, 'Map Size/Type');
  return '';
}

/**
 * Size/Type sub-line for the detail views (EDI Info modal, EDI Order drawer): the
 * combined value + a pencil to re-map the edi_equipmentmap record; or a red "Map
 * Size/Type" button when there's a code but no size/type yet; else nothing.
 */
export function renderMappedSizeTypeLine(
  sizeType: unknown,
  equipmentMapCode: unknown,
  onMapEquipment?: (recno: number) => void,
): React.ReactNode {
  const nm = sizeType == null ? '' : String(sizeType).trim();
  const recno = Number(equipmentMapCode) || 0;
  if (nm) {
    return (
      <span className="inline-flex items-center gap-1">
        {nm}
        {recno > 0 && onMapEquipment && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMapEquipment(recno); }}
            className="ml-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            title="Re-map size / type"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }
  if (recno > 0 && onMapEquipment) return mapActionButton(recno, onMapEquipment, 'Map Size/Type');
  return null;
}

/** The red "map me" action button keyed by a STRING (the port's unlocode) rather than a
 *  recno — ports map by stamping the unlocode onto a cms.ports row (V1 edi_savemapport),
 *  so there is no map-table recno. Otherwise identical to mapActionButton. */
function mapPortButton(unlocode: string, onMapPort: (u: string) => void, label: string): React.ReactNode {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onMapPort(unlocode); }}
      className="bg-brand-red text-white rounded px-1.5 py-0.5 text-xs font-medium hover:opacity-90"
      title={`${label} (${unlocode})`}
    >
      {label}
    </button>
  );
}

/**
 * Render a port value the way V1 %portdata does: the mapped CMS port name when a non-deleted
 * cms.ports row carries this UN/LOCODE; otherwise, if the row has a raw unlocode, a red "Map
 * Port" link (bg-brand-red) that opens the port-map modal for that unlocode; else blank.
 * Used on the board (card + addable table columns).
 */
export function renderPortValue(
  name: unknown,
  unlocode: unknown,
  onMapPort?: (unlocode: string) => void,
): React.ReactNode {
  const nm = name == null ? '' : String(name).trim();
  if (nm) return nm;
  const code = unlocode == null ? '' : String(unlocode).trim();
  if (code && onMapPort) return mapPortButton(code, onMapPort, 'Map Port');
  return code; // no handler → show the raw unlocode
}

/**
 * Port sub-line for the detail views (EDI Info modal, EDI Order drawer): the mapped port
 * name + a pencil to re-map (re-opens the modal for the unlocode); or a red "Map Port"
 * button when there's a raw unlocode but no CMS port yet; else the raw unlocode / nothing.
 */
export function renderMappedPortLine(
  name: unknown,
  unlocode: unknown,
  onMapPort?: (unlocode: string) => void,
): React.ReactNode {
  const nm = name == null ? '' : String(name).trim();
  const code = unlocode == null ? '' : String(unlocode).trim();
  if (nm) {
    return (
      <span className="inline-flex items-center gap-1">
        {nm}
        {code && onMapPort && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMapPort(code); }}
            className="ml-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            title="Re-map this port"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }
  if (code && onMapPort) return mapPortButton(code, onMapPort, 'Map Port');
  return code || null;
}

/**
 * SCAC for the board (card + table): the resolved SCAC when present; otherwise, if the row
 * carries an edi_scacmap recno (mappedscac — an SS-line text that hasn't been mapped to a
 * CMS SCAC yet), a red "Map SCAC" button opening the edi_scacmap editor; else blank. Mirrors
 * renderSizeTypeValue — mappedscac is the map key, exactly like equipmentmaprecno.
 */
export function renderScacValue(
  scac: unknown,
  mappedScacRecno: unknown,
  onMapScac?: (recno: number) => void,
): React.ReactNode {
  const nm = scac == null ? '' : String(scac).trim();
  if (nm) return nm;
  const recno = Number(mappedScacRecno) || 0;
  if (recno > 0 && onMapScac) return mapActionButton(recno, onMapScac, 'Map SCAC');
  return '';
}

/**
 * SCAC sub-line for the detail views (EDI Info modal, EDI Order drawer): the resolved SCAC
 * + a pencil to re-map the edi_scacmap record; or a red "Map SCAC" button when there's a
 * mapping row but no SCAC yet; else nothing. Mirrors renderMappedSizeTypeLine.
 */
export function renderMappedScacLine(
  scac: unknown,
  mappedScacRecno: unknown,
  onMapScac?: (recno: number) => void,
): React.ReactNode {
  const nm = scac == null ? '' : String(scac).trim();
  const recno = Number(mappedScacRecno) || 0;
  if (nm) {
    return (
      <span className="inline-flex items-center gap-1">
        {nm}
        {recno > 0 && onMapScac && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMapScac(recno); }}
            className="ml-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            title="Re-map SCAC"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }
  if (recno > 0 && onMapScac) return mapActionButton(recno, onMapScac, 'Map SCAC');
  return null;
}

export function buildEdiColumns(
  fields: EdiFieldDefinition[],
  opts: BuildEdiColumnsOptions = {},
): ColumnDef<any>[] {
  const displayable = fields.filter((f) => f.is_displayable !== false && accessorFor(f));
  const byAccessor = new Map<string, EdiFieldDefinition>();
  for (const f of displayable) {
    const a = accessorFor(f);
    if (!byAccessor.has(a)) byAccessor.set(a, f); // guard against dup accessors
  }

  const defaults = EDI_DEFAULT_COLUMNS.filter((a) => byAccessor.has(a));
  const rest = [...byAccessor.keys()]
    .filter((a) => !defaults.includes(a))
    .sort((a, b) => (byAccessor.get(a)!.displayname || a).localeCompare(byAccessor.get(b)!.displayname || b));

  const built = [...defaults, ...rest].map((accessor) => {
    const field = byAccessor.get(accessor)!;
    const special = SPECIAL_COLUMN_BUILDERS[accessor];
    if (special) return special(field);
    if (accessor === 'editype' && opts.onEdiInfoClick) {
      const onClick = opts.onEdiInfoClick;
      return {
        id: 'editype',
        accessorKey: 'editype',
        header: field.abbreviation || field.displayname || 'EDI Type',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original as any;
          const v = info.getValue() as string;
          // Blue multi-delivery flag (V1 highlighted the whole row blue; a badge keeps that
          // meaning without colliding with the accept/reject urgency row tint). Pinned here on
          // the always-visible EDI Type cell so it can't be hidden by column config. `multistop`
          // is the board's computed "> 1 delivery in contactinformation" flag.
          const flag = Number(row.multistop) > 0 ? (
            <span
              title="Multiple delivery locations — review before creating/attaching"
              aria-label="Multiple delivery locations"
              className="shrink-0 text-blue-600 dark:text-blue-400"
            >
              <FlagIcon className="w-3.5 h-3.5" />
            </span>
          ) : null;
          if (!v && !flag) return '';
          return (
            <span className="inline-flex items-center gap-1">
              {flag}
              {v && (
                <button
                  type="button"
                  onClick={() => onClick(row.recno)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  title="View EDI Info"
                >
                  {v}
                </button>
              )}
            </span>
          );
        },
      } as ColumnDef<any>;
    }
    if (accessor === 'sizetype') {
      return {
        id: 'sizetype',
        accessorKey: 'sizetype',
        header: field.abbreviation || field.displayname || 'Size/Type',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => {
          const row = info.row.original as any;
          return renderSizeTypeValue(row.size, row.equiptype, row.equipmentmaprecno, opts.onMapEquipment);
        },
      } as ColumnDef<any>;
    }
    if (accessor === 'scac') {
      return {
        id: 'scac',
        accessorKey: 'scac',
        header: field.abbreviation || field.displayname || 'SCAC',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => renderScacValue(info.getValue(), (info.row.original as any).mappedscac, opts.onMapScac),
      } as ColumnDef<any>;
    }
    if (accessor === 'ediportoforigin' || accessor === 'ediportofdischarge') {
      const nameField = accessor === 'ediportoforigin' ? 'portoforiginname' : 'portofdischargename';
      return {
        id: accessor,
        accessorKey: accessor,
        header: field.abbreviation || field.displayname || accessor,
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => renderPortValue((info.row.original as any)[nameField], info.getValue(), opts.onMapPort),
      } as ColumnDef<any>;
    }
    const codeField = LOCATION_CODE_FIELD[accessor];
    if (codeField && opts.onMapLocation) {
      const onMap = opts.onMapLocation;
      return {
        id: accessor,
        accessorKey: accessor,
        header: field.abbreviation || field.displayname || accessor,
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => renderLocationValue(info.getValue(), (info.row.original as any)[codeField], onMap),
      } as ColumnDef<any>;
    }
    const col: ColumnDef<any> = {
      id: accessor,
      accessorKey: accessor,
      header: field.abbreviation || field.displayname || accessor,
      enableSorting: true,
      enableColumnFilter: true,
      cell: cellFor(field),
      meta:
        field.type === 'number' || field.type === 'decimal'
          ? { align: 'right' }
          : undefined,
    };
    return col;
  });

  // Template column — the set template value as a link that reopens the picker to change or
  // clear it (blank rows use the Get Template action instead). Not a registry field.
  if (opts.onEditTemplate) {
    const onEdit = opts.onEditTemplate;
    built.push({
      id: '__template',
      header: 'Template',
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      cell: (info) => {
        const row = info.row.original as any;
        const t = ((row.template as string) || '').trim();
        if (!t) return '';
        return (
          <button
            type="button"
            onClick={() => onEdit(row.recno)}
            className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            title="Change or clear this template"
          >
            {t}
          </button>
        );
      },
    } as ColumnDef<any>);
  }

  // Customer master assignment, independent of EDI lane ownership. Like Owner,
  // this bespoke query field is offered without a field-definition registry row.
  built.push({
    id: 'customer_lc',
    accessorKey: 'customer_lc',
    header: 'Customer LC',
    enableSorting: true,
    enableColumnFilter: true,
    cell: (info) => info.getValue<string>() || '',
  });

  // Lane OWNER — which LC(s) this order belongs to under cms.edi_csr_rules, plus an
  // "Unclaimed" marker when no rule on the handle claims it.
  //
  // Most useful in the UNSCOPED view (a manager seeing who covers what) and when
  // answering "why isn't this in my list" — when an LC is filtered to themselves every
  // row is theirs, so the column is redundant. Hideable, and hidden by default while
  // the rules table is still empty: with no rules every row's owner list is just the
  // handle's roster, which is already on the bucket. Once handles start carrying real
  // rules, flip its entry in buildEdiDefaultVisibility to true and bump
  // RESULTS_SETTINGS_KEY so saved layouts pick it up.
  built.push({
    id: '__owner',
    header: 'Owner',
    enableSorting: true,
    enableColumnFilter: true,
    accessorFn: (row: any) => (row.owners as string) || '',
    cell: (info) => {
      const row = info.row.original as any;
      const owners = ((row.owners as string) || '').trim();
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className={owners ? '' : 'text-gray-400 dark:text-gray-500'}>{owners || '—'}</span>
          {!!row.unclaimed && (
            <span
              className="whitespace-nowrap rounded px-1 py-px text-[11px] font-medium text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40"
              title="No lane rule on this handle matches this order — it falls to the handle's catch-all LCs"
            >
              Unclaimed
            </span>
          )}
        </span>
      );
    },
  } as ColumnDef<any>);

  // Right-pinned per-record Actions column (Accept/Reject/Delete or Clear/Delete),
  // appended only when the host wires the handlers. Not a registry field → not hideable.
  const hasRowActions = !!(opts.onAcceptRow || opts.onRejectRow || opts.onDeleteRow || opts.onClearRow || opts.onGetTemplateRow || opts.onAttachRow || opts.onCreateRow);
  if (hasRowActions) {
    built.push({
      id: '__actions',
      header: 'Actions',
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      cell: (info) => {
        const row = info.row.original as any;
        return (
          <EdiRowActions
            acceptreject={row.acceptreject}
            onAccept={opts.onAcceptRow ? () => opts.onAcceptRow!(row.recno) : undefined}
            onReject={opts.onRejectRow ? () => opts.onRejectRow!(row.recno) : undefined}
            onDelete={opts.onDeleteRow ? () => opts.onDeleteRow!(row.recno) : undefined}
            onClear={opts.onClearRow ? () => opts.onClearRow!(row.recno) : undefined}
            onGetTemplate={opts.onGetTemplateRow ? () => opts.onGetTemplateRow!(row.recno) : undefined}
            canGetTemplate={canGetTemplate(row)}
            onAttach={opts.onAttachRow ? () => opts.onAttachRow!(row.recno) : undefined}
            canAttach={canAttach(row)}
            onCreate={opts.onCreateRow ? () => opts.onCreateRow!(row.recno) : undefined}
            canCreate={canCreate(row)}
            compact
          />
        );
      },
    } as ColumnDef<any>);
  }
  return built;
}

/** VisibilityState hiding every non-default column (TanTable treats missing keys as
 *  visible, so non-defaults must be explicitly false). */
export function buildEdiDefaultVisibility(fields: EdiFieldDefinition[]): VisibilityState {
  const vis: VisibilityState = {};
  for (const f of fields) {
    const a = accessorFor(f);
    if (!a) continue;
    vis[a] = EDI_DEFAULT_COLUMNS.includes(a);
  }
  // Lane Owner is a computed column, so it is not in `fields` and would otherwise
  // default to visible. Kept off until handles actually carry lane rules — until then
  // it only repeats the handle roster already shown on the bucket. Flip to true (and
  // bump RESULTS_SETTINGS_KEY) at rollout.
  vis['__owner'] = false;
  vis.customer_lc = false;
  return vis;
}

/** availableFields payload for the TanTable column picker (fieldname === accessor so
 *  show/hide toggles line up with the generated column ids). */
export function buildEdiAvailableFields(fields: EdiFieldDefinition[]) {
  return [
    ...fields
      .filter((f) => f.is_displayable !== false && accessorFor(f))
      .map((f) => ({
        fieldname: accessorFor(f),
        displayname: f.displayname,
        abbreviation: f.abbreviation,
        type: f.type,
      })),
    // Computed, so it has no registry row — listed explicitly or the picker could never
    // turn it on, and it ships hidden (see buildEdiDefaultVisibility).
    { fieldname: '__owner', displayname: 'Owner (Lane LC)', abbreviation: 'Owner', type: 'text' },
    { fieldname: 'customer_lc', displayname: 'Customer LC (CSR)', abbreviation: 'Customer LC', type: 'string' },
  ];
}

// =============================================================================
// Card (Multi Row) view — TanTable card mode renders each visible column as a card
// field; the value renderer below is the card counterpart of the table cells above.
// =============================================================================

/** Handlers the card fields need (the link / map-action cells). */
export interface EdiCardHandlers {
  onEdiInfoClick?: (recno: number) => void;
  onMapLocation?: (recno: number) => void;
  onMapEquipment?: (recno: number) => void;
  onMapPort?: (unlocode: string) => void;
  onMapScac?: (recno: number) => void;
  onEditTemplate?: (recno: number) => void;
}

/** Card fields shown mono (equipment / reference numbers). */
export const EDI_CARD_MONO_IDS = new Set<string>([
  'container', 'scac', 'bol', 'booking', 'po', 'billtorefno', 'sealnumber',
  'pickupnumber', 'voyage', 'sapordernumber', 'brokerrefno',
]);

/** Fields visible by default in the card view (mirrors the legacy strip). Non-registry
 *  columns (__template) aren't listed → TanTable defaults them visible; the raw `template`
 *  string field is left out so only the editable __template link shows. */
export const EDI_CARD_DEFAULT_COLUMNS: string[] = [
  'editype', 'dupeprolist', 'processdatetime', 'container', 'movetype', 'rail', 'scac',
  'bol', 'booking', 'billtoname', 'sizetype', 'po', 'vessel', 'billtorefno', 'commodity',
  'ediportoforigin', 'ediportofdischarge', 'hazmat', 'rateinfo', 'lbs', 'kgs', 'etadate',
  'earliestpickup', 'lastfreedaydate', 'cutoffdatetime', 'pickupname', 'deliveryname',
  'terminatename', 'acceptedby', 'acceptdatetime',
];

/**
 * Render ONE field's value for a card. The special fields (EDI Info / dupes links, the
 * Size-Type / SCAC / port / location map cells, the template edit link) mirror their table
 * cells; everything else is formatted by its registry `type`, else shown raw. `type` comes
 * from the field-definition registry. Links stopPropagation so they don't also toggle the
 * card's click-to-highlight.
 */
export function renderEdiCardValue(id: string, row: any, h: EdiCardHandlers, type?: string): React.ReactNode {
  switch (id) {
    case 'editype': {
      const v = (row.editype as string) || '';
      if (!v || !h.onEdiInfoClick) return v;
      return (
        <button type="button" onClick={(e) => { e.stopPropagation(); h.onEdiInfoClick!(row.recno); }}
          className="text-blue-600 dark:text-blue-400 hover:underline" title="View EDI Info">{v}</button>
      );
    }
    case 'dupeprolist': {
      const pros = combineDupePros(row.dupeprolist, row.dupeprolist2);
      if (!pros.length) return '';
      return (
        <button type="button" onClick={(e) => { e.stopPropagation(); openDupeProSearch(pros); }}
          className="text-blue-600 dark:text-blue-400 hover:underline tabular-nums"
          title={`View ${pros.length} duplicate pro${pros.length === 1 ? '' : 's'} in a new tab`}>{pros.length}</button>
      );
    }
    case 'sizetype': return renderSizeTypeValue(row.size, row.equiptype, row.equipmentmaprecno, h.onMapEquipment);
    case 'scac': return renderScacValue(row.scac, row.mappedscac, h.onMapScac);
    case 'ediportoforigin': return renderPortValue(row.portoforiginname, row.ediportoforigin, h.onMapPort);
    case 'ediportofdischarge': return renderPortValue(row.portofdischargename, row.ediportofdischarge, h.onMapPort);
    case 'pickupname': return renderLocationValue(row.pickupname, row.pickupcode, h.onMapLocation);
    case 'deliveryname': return renderLocationValue(row.deliveryname, row.deliverycode, h.onMapLocation);
    case 'terminatename': return renderLocationValue(row.terminatename, row.terminatecode, h.onMapLocation);
    case 'template':
    case '__template': {
      const t = ((row.template as string) || '').trim();
      if (!t) return '';
      if (!h.onEditTemplate) return t;
      return (
        <button type="button" onClick={(e) => { e.stopPropagation(); h.onEditTemplate!(row.recno); }}
          className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium" title="Change or clear this template">{t}</button>
      );
    }
    default: {
      const v = row[id];
      if (type === 'date') return formatDate(v as string);
      if (type === 'datetime') return formatDateTime(v as string);
      if (type === 'number' || type === 'decimal') return fmtNumber(v);
      return v === null || v === undefined ? '' : (v as React.ReactNode);
    }
  }
}

/** VisibilityState for the card view (like buildEdiDefaultVisibility but the card set). */
export function buildEdiCardDefaultVisibility(fields: EdiFieldDefinition[]): VisibilityState {
  const vis: VisibilityState = {};
  for (const f of fields) {
    const a = accessorFor(f);
    if (!a) continue;
    vis[a] = EDI_CARD_DEFAULT_COLUMNS.includes(a);
  }
  vis.customer_lc = false;
  return vis;
}
